import { api } from './api';
import { pickTranslation, type Mood, type TranslationMap } from '../types/game';

/**
 * Everything the app knows about the moods endpoint lives here, so a change to
 * the contract is a change to this file (and the settings thunks) only.
 */

export type MoodAvailability =
  | 'ok'
  /** Tied to remote play, which has not shipped yet. */
  | 'remote'
  /** No cards, and explicit cards are off: turning them on may help. */
  | 'explicit'
  /** No cards, and explicit cards are already on. */
  | 'empty'
  /** Too few cards to start (`playable` is false), and explicit cards are off. */
  | 'fewExplicit'
  /** Too few cards to start, and explicit cards are already on. */
  | 'few';

function isTranslationMap(value: unknown): value is TranslationMap {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toMood(raw: unknown): Mood | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const item = raw as Record<string, unknown>;
  if (typeof item.slug !== 'string' || item.slug === '') return null;
  const context = item.play_context;
  const cardCount = typeof item.card_count === 'number' ? item.card_count : 0;
  return {
    id: typeof item.id === 'number' ? item.id : 0,
    slug: item.slug,
    title: isTranslationMap(item.title) ? item.title : {},
    subtitle: isTranslationMap(item.subtitle) ? item.subtitle : {},
    category_slugs: Array.isArray(item.category_slugs)
      ? item.category_slugs.filter(
          (slug): slug is string => typeof slug === 'string'
        )
      : [],
    spice_min: typeof item.spice_min === 'number' ? item.spice_min : 1,
    spice_max: typeof item.spice_max === 'number' ? item.spice_max : 10,
    play_context:
      context === 'in_person' || context === 'remote' ? context : 'any',
    round_limit: typeof item.round_limit === 'number' ? item.round_limit : null,
    image_url: typeof item.image_url === 'string' ? item.image_url : null,
    card_count: cardCount,
    // An API without the flag counts any mood that has cards as playable.
    playable:
      typeof item.playable === 'boolean' ? item.playable : cardCount > 0,
  };
}

/** Accepts the bare array the API returns, or a `{ data: [...] }` wrapper. */
export function normalizeMoods(payload: unknown): Mood[] {
  const list = Array.isArray(payload)
    ? payload
    : payload &&
        typeof payload === 'object' &&
        Array.isArray((payload as { data?: unknown }).data)
      ? (payload as { data: unknown[] }).data
      : [];
  return list.map(toMood).filter((mood): mood is Mood => mood !== null);
}

/** GET /moods */
export async function requestMoods(): Promise<Mood[]> {
  const response = await api.get('/moods');
  return normalizeMoods(response.data);
}

export function moodTitle(mood: Mood, language: string): string {
  return pickTranslation(mood.title, language) || mood.slug;
}

export function moodSubtitle(mood: Mood, language: string): string {
  return pickTranslation(mood.subtitle, language);
}

export function getMoodAvailability(
  mood: Mood,
  options: { showExplicit: boolean; remoteAvailable: boolean }
): MoodAvailability {
  if (mood.play_context === 'remote' && !options.remoteAvailable) {
    return 'remote';
  }
  if (mood.card_count === 0) {
    return options.showExplicit ? 'empty' : 'explicit';
  }
  if (!mood.playable) {
    return options.showExplicit ? 'few' : 'fewExplicit';
  }
  return 'ok';
}
